import { COUNTRY_REGIONS } from './constants';

// Regions that trigger strict GDPR-style consent semantics:
//   - default-denied non-essential categories
//   - "dismiss" never counts as accept-all
//   - first-layer banner must surface a Reject option
const STRICT_REGION_KEYS = ['europe', 'uk'];

const REGION_STATE_KEY = '__acbRegion';
const REGION_PROMISE_KEY = '__acbRegionPromise';

export function detectDeviceClient() {
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    if (!ua) return 'unknown';
    if (ua.includes('ipad') || (ua.includes('tablet') && !ua.includes('mobile'))) return 'tablet';
    if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) return 'mobile';
    return 'desktop';
  } catch (_) {
    return 'unknown';
  }
}

function deriveRegionState(visitorRegions, selectedRegions, countryIso, device) {
  const safeVisitor = Array.isArray(visitorRegions) ? visitorRegions : [];
  const safeSelected = Array.isArray(selectedRegions) && selectedRegions.length
    ? selectedRegions
    : ['worldwide'];

  const inRegion = safeSelected.includes('worldwide')
    || safeSelected.some(r => safeVisitor.includes(r));

  const isStrict = safeVisitor.some(r => STRICT_REGION_KEYS.includes(r));

  return {
    inRegion,
    isStrict,
    visitorRegions: safeVisitor,
    countryIso:     countryIso || null,
    device:         device || detectDeviceClient(),
  };
}

function cacheState(state) {
  try {
    window[REGION_STATE_KEY] = state;
  } catch (_) {}
  return state;
}

function parseShopifyCountryPayload(data) {
  const country = data?.detected_values?.country;
  const raw = country && (country.handle != null && country.handle !== ''
    ? country.handle
    : country.iso_code);
  return raw != null && raw !== '' ? String(raw).toUpperCase() : null;
}

/**
 * Shopify stores expose /browsing_context_suggestions.json; BigCommerce does not.
 * Try Shopify first, then IP geolocation so analytics country + region gating work on BC.
 */
function fetchVisitorCountryIso() {
  return fetch('/browsing_context_suggestions.json')
    .then(r => (r.ok ? r.json() : Promise.reject(new Error('shopify geo unavailable'))))
    .then(data => parseShopifyCountryPayload(data))
    .then(alpha2 => {
      if (alpha2) return alpha2;
      return Promise.reject(new Error('shopify geo empty'));
    })
    .catch(() =>
      fetch('https://ipinfo.io/json', { credentials: 'omit' })
        .then(r => (r.ok ? r.json() : Promise.reject(new Error('ip geo unavailable'))))
        .then(data => {
          const code = data?.country;
          return code ? String(code).toUpperCase() : null;
        }),
    );
}

/**
 * Returns a Promise that resolves to { inRegion, isStrict, visitorRegions, countryIso }.
 * Cached on window.__acbRegion so the same fetch never runs twice and so other
 * modules (gating.js, banner.js) can read the result synchronously after the
 * first call resolves.
 */
export function getRegionState(config = {}) {
  if (window[REGION_STATE_KEY]) {
    return Promise.resolve(window[REGION_STATE_KEY]);
  }
  if (window[REGION_PROMISE_KEY]) {
    return window[REGION_PROMISE_KEY];
  }

  const selectedRegions = config.selected_regions || ['worldwide'];

  // Strict-mode detection always uses the visitor's actual location, not the
  // merchant's selected regions, so EU visitors get strict semantics even when
  // the banner is configured for "worldwide".
  const promise = fetchVisitorCountryIso()
    .then(alpha2 => {
      const visitorRegions = alpha2 ? (COUNTRY_REGIONS[alpha2] || []) : [];
      return cacheState(deriveRegionState(visitorRegions, selectedRegions, alpha2, detectDeviceClient()));
    })
    .catch(() => {
      // On detection failure, fall back to: show the banner (cautious) and do
      // NOT assume strict mode (we can't enforce strict UX globally without
      // legal scope creep). Merchants in EU should set selected_regions
      // accordingly to keep strict UX guaranteed.
      return cacheState(deriveRegionState([], selectedRegions, null, detectDeviceClient()));
    });

  try { window[REGION_PROMISE_KEY] = promise; } catch (_) {}
  return promise;
}

/**
 * Synchronous read of the cached region state. Returns null if detection has
 * not finished yet — callers must handle that case (gating.js installs strict
 * defaults regardless until the result is known).
 */
export function getRegionStateSync() {
  return window[REGION_STATE_KEY] || null;
}

/**
 * Logs detected shopper location (country + mapped consent regions). Pass
 * `resolvedState` from getRegionState().then(...) on first paint so the log is
 * never stale; omit it after geo has cached (e.g. consent button clicks).
 */
export function logVisitorRegion(message, resolvedState) {
  if (typeof console === 'undefined' || typeof console.log !== 'function') return;
  const s = resolvedState != null ? resolvedState : getRegionStateSync();
  const visitor = s && Array.isArray(s.visitorRegions) ? s.visitorRegions : [];
  const iso = s && s.countryIso ? s.countryIso : null;
  if (message === 'site open') {
    const regionsLabel = visitor.length ? visitor.join(', ') : 'none';
    // console.log(
    //   '[Access Pro] Store opening from — country:',
    //   iso || 'not detected',
    //   '| regions:',
    //   regionsLabel,
    // );
    return;
  }
  // console.log('[Access Pro] user region:', message, { countryIso: iso, visitorRegions: visitor });
}

/**
 * Backwards-compatible region check used by index.js to decide whether to show
 * the banner at all. Re-uses getRegionState so we never double-fetch.
 */
export function checkRegion(config) {
  return getRegionState(config).then(s => s.inRegion);
}
